/**
 * Whether this browser holds a session, and how to start or end one.
 *
 * The server is the authority. Nothing here decides that somebody is signed
 * in — it asks, and reports the answer. A client that kept its own flag would
 * eventually disagree with the server, and it would disagree in the direction
 * of showing a screen to somebody who is no longer allowed to see it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { readSession, signIn, signOut } from "@/shared/api";
import { isUnauthorized, statusOf } from "@/shared/http/errors";

import { clearViewAs, readViewAs } from "@/shared/session/viewAs";

export const SESSION_KEY = ["session"] as const;

export type SessionState =
  | { readonly status: "checking" }
  | {
      readonly status: "signed-in";
      readonly expiresAt: string;
      readonly login: string;
      readonly tenantId: string;
      readonly accountId: string;
      readonly roles: readonly string[];
      /** A platform administrator looking at this account, read only. */
      readonly viewing: { readonly name: string } | null;
      /** A client: the account's person is not the platform's administrator,
       *  or the administrator is looking at it as its client sees it. */
      readonly client: boolean;
    }
  | { readonly status: "platform"; readonly login: string; readonly expiresAt: string; readonly canPublish: boolean }
  | { readonly status: "signed-out" };

export function useSession(): SessionState {
  const query = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      const { data, error, response } = await readSession();
      if (response?.status === 401) return null;
      // A view the server no longer allows (client removed, owner disabled):
      // drop it and fall back to the administrator's own session.
      if ((response?.status === 403 || response?.status === 409) && readViewAs() !== null) {
        clearViewAs();
        const retried = await readSession();
        if (retried.response?.status === 401) return null;
        if (retried.data === undefined) throw new Error("session check failed");
        return retried.data;
      }
      if (error !== undefined || data === undefined) {
        throw new Error("session check failed");
      }
      return data;
    },
    // Asked once on load and after signing in or out. Polling it would turn
    // "am I still signed in" into steady traffic for an answer that only
    // changes when something else already told us.
    staleTime: Infinity,
    retry: false,
  });

  if (query.isPending) return { status: "checking" };
  if (query.data == null) return { status: "signed-out" };
  if (query.data.tenant_id === null && query.data.account_id === null && query.data.platform_permissions?.includes("PLATFORM_ADMIN")) {
    return {
      status: "platform",
      login: query.data.login,
      expiresAt: query.data.expires_at,
      canPublish: query.data.platform_permissions.includes("PUBLISH_PLATFORM_STRATEGY"),
    };
  }
  if (query.data.tenant_id == null || query.data.account_id == null) return { status: "signed-out" };
  return {
    status: "signed-in",
    expiresAt: query.data.expires_at,
    login: query.data.login,
    tenantId: query.data.tenant_id,
    accountId: query.data.account_id,
    roles: query.data.roles,
    viewing: query.data.viewing_as_platform === true ? { name: readViewAs()?.name ?? query.data.tenant_id } : null,
    client: !(query.data.platform_permissions ?? []).includes("PLATFORM_ADMIN"),
  };
}

export type SignInFailure = "wrong" | "locked" | "service" | "unreachable";

export type SignInCredentials = {
  readonly login: string;
  readonly tenantId: string | null;
  readonly accountId: string | null;
  readonly password: string;
};

export function useSignIn(): {
  readonly submit: (credentials: SignInCredentials) => void;
  readonly pending: boolean;
  readonly failure: SignInFailure | null;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (credentials: SignInCredentials) => {
      clearViewAs();
      const { error, response } = await signIn({
        body: {
          login: credentials.login,
          tenant_id: credentials.tenantId,
          account_id: credentials.accountId,
          password: credentials.password,
        },
      });
      if (error !== undefined || (response?.status ?? 0) >= 400) {
        throw Object.assign(new Error("sign in refused"), {
          status: response?.status,
        });
      }
    },
    onSuccess: async () => {
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] !== SESSION_KEY[0],
      });
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== SESSION_KEY[0],
      });
      // The session is re-read rather than assumed: the deadline shown in the
      // top bar comes from the server, and inventing it here would let the
      // screen and the server disagree about when this ends.
      await queryClient.invalidateQueries({ queryKey: SESSION_KEY });
    },
  });

  return {
    submit: (credentials: SignInCredentials) => {
      mutation.mutate(credentials);
    },
    pending: mutation.isPending,
    failure: mutation.error === null ? null : classify(mutation.error),
  };
}

function classify(error: unknown): SignInFailure {
  const status = statusOf(error);
  if (status === 429) return "locked";
  if (isUnauthorized(error)) return "wrong";
  if (status !== null && status >= 500) return "service";
  return "unreachable";
}

/**
 * Signing out. A failed request leaves the owner signed in, because it means
 * the session on the server is still live and saying otherwise would be a
 * comforting screen over a session somebody else could still use.
 */
export function useSignOut(): { readonly submit: () => void; readonly pending: boolean } {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      // Writes are refused while viewing; signing out ends the view first.
      clearViewAs();
      const { error, response } = await signOut();
      if (error !== undefined || (response?.status ?? 0) >= 400) {
        throw new Error("sign out refused");
      }
    },
    onSuccess: () => {
      // Everything cached was read as a signed-in owner. Kept after signing
      // out, it would still be on screen for whoever is at the machine next.
      //
      // Everything *except* the session itself. `clear()` would take that too,
      // and clearing a query detaches the component watching it — the next
      // write would then have nobody to tell, and the account would stay on
      // screen with no session behind it.
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== SESSION_KEY[0],
      });
      // Set rather than re-asked. The server has just said the session is
      // gone; a round trip to be told again is a round trip during which the
      // account is still on screen.
      queryClient.setQueryData(SESSION_KEY, null);
    },
  });

  return {
    submit: () => {
      mutation.mutate();
    },
    pending: mutation.isPending,
  };
}
